import { AudioManager } from '../../engine/audio/AudioManager';

export interface GameControlsCallbacks {
  onPauseToggle: () => void;
  onRestart: () => void;
  onDirectionInput?: (dir: 'up' | 'down' | 'left' | 'right' | 'action') => void;
}

export class GameControls {
  private container: HTMLElement;
  private callbacks: GameControlsCallbacks;
  private isMuted = AudioManager.muted;
  private isPaused = false;

  constructor(container: HTMLElement, callbacks: GameControlsCallbacks) {
    this.container = container;
    this.callbacks = callbacks;
    this.initKeyboardShortcuts();

    // Listen for fullscreen change to update button text
    document.addEventListener('fullscreenchange', () => {
      this.render();
    });
  }

  render(): void {
    const isFullscreen = !!document.fullscreenElement;

    this.container.innerHTML = `
      <div class="game-control-bar">
        <!-- Sol Buton Grubu: Kontroller -->
        <div class="control-group">
          <button id="ctrl-pause-btn" class="ctrl-btn" title="Duraklat / Devam Et (Kısayol: P veya ESC)">
            <span class="ctrl-btn__icon">${this.isPaused ? '▶' : '⏸'}</span>
            <span class="ctrl-btn__label">${this.isPaused ? 'Devam Et' : 'Duraklat'}</span>
            <span class="ctrl-btn__badge">P</span>
          </button>

          <button id="ctrl-restart-btn" class="ctrl-btn" title="Yeniden Başlat (Kısayol: R)">
            <span class="ctrl-btn__icon">🔄</span>
            <span class="ctrl-btn__label">Yeniden Başlat</span>
            <span class="ctrl-btn__badge">R</span>
          </button>
        </div>

        <!-- Sağ Buton Grubu: Ses & Tam Ekran -->
        <div class="control-group">
          <button id="ctrl-audio-btn" class="ctrl-btn" title="Sesi Aç/Kapat (Kısayol: M)">
            <span class="ctrl-btn__icon">${this.isMuted ? '🔇' : '🔊'}</span>
            <span class="ctrl-btn__label">${this.isMuted ? 'Ses Kapalı' : 'Ses Açık'}</span>
            <span class="ctrl-btn__badge">M</span>
          </button>

          <button id="ctrl-fullscreen-btn" class="ctrl-btn" title="Tam Ekran (Kısayol: F)">
            <span class="ctrl-btn__icon">${isFullscreen ? '🗗' : '⛶'}</span>
            <span class="ctrl-btn__label">${isFullscreen ? 'Çıkış' : 'Tam Ekran'}</span>
            <span class="ctrl-btn__badge">F</span>
          </button>
        </div>
      </div>

      <!-- Mobil Sanal D-Pad ve Dokunmatik Kontrol Paneli -->
      <div class="virtual-dpad-container">
        <div class="dpad-grid">
          <div></div>
          <button data-dir="up" class="dpad-btn dpad-btn--up">▲</button>
          <div></div>
          <button data-dir="left" class="dpad-btn dpad-btn--left">◀</button>
          <button data-dir="down" class="dpad-btn dpad-btn--down">▼</button>
          <button data-dir="right" class="dpad-btn dpad-btn--right">▶</button>
        </div>
        <div class="action-btn-container">
          <button data-dir="action" class="action-btn">BOŞLUK / AKSİYON</button>
        </div>
      </div>
    `;

    this.attachEvents();
  }

  private attachEvents(): void {
    const pauseBtn = this.container.querySelector('#ctrl-pause-btn');
    pauseBtn?.addEventListener('click', () => this.togglePause());

    const restartBtn = this.container.querySelector('#ctrl-restart-btn');
    restartBtn?.addEventListener('click', () => this.callbacks.onRestart());

    const audioBtn = this.container.querySelector('#ctrl-audio-btn');
    audioBtn?.addEventListener('click', () => this.toggleAudio());

    const fsBtn = this.container.querySelector('#ctrl-fullscreen-btn');
    fsBtn?.addEventListener('click', () => this.toggleFullscreen());

    // Mobil D-Pad dokunmatik eventleri
    this.container.querySelectorAll('[data-dir]').forEach((btn) => {
      const dir = btn.getAttribute('data-dir') as 'up' | 'down' | 'left' | 'right' | 'action';

      const handlePress = (e: Event) => {
        e.preventDefault();
        AudioManager.playBeep(400, 0.04);
        this.callbacks.onDirectionInput?.(dir);
        this.dispatchSyntheticKey(dir);
      };

      btn.addEventListener('touchstart', handlePress, { passive: false });
      btn.addEventListener('mousedown', handlePress);
    });
  }

  private dispatchSyntheticKey(dir: 'up' | 'down' | 'left' | 'right' | 'action'): void {
    const keyMap = {
      up: 'ArrowUp',
      down: 'ArrowDown',
      left: 'ArrowLeft',
      right: 'ArrowRight',
      action: ' ',
    };
    const key = keyMap[dir];
    window.dispatchEvent(new KeyboardEvent('keydown', { key }));
  }

  private togglePause(): void {
    this.isPaused = !this.isPaused;
    this.callbacks.onPauseToggle();
    this.render();
  }

  private toggleAudio(): void {
    this.isMuted = AudioManager.toggleMute();
    this.render();
  }

  private toggleFullscreen(): void {
    const targetElement = document.getElementById('game-fullscreen-wrapper') || document.querySelector('.game-view-layout') || document.documentElement;

    if (!document.fullscreenElement) {
      targetElement.requestFullscreen?.().then(() => {
        // Attempt to lock to landscape on mobile devices
        if (screen.orientation && (screen.orientation as any).lock) {
          (screen.orientation as any).lock('landscape').catch(() => {});
        }
      }).catch((err) => {
        console.warn('Fullscreen request failed:', err);
      });
    } else {
      document.exitFullscreen?.().then(() => {
        if (screen.orientation && (screen.orientation as any).unlock) {
          (screen.orientation as any).unlock();
        }
      }).catch(() => {});
    }
  }

  private initKeyboardShortcuts(): void {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) return;

      const key = e.key.toLowerCase();
      if (key === 'm') {
        e.preventDefault();
        this.toggleAudio();
      } else if (key === 'f') {
        e.preventDefault();
        this.toggleFullscreen();
      } else if (key === 'p' || e.key === 'Escape') {
        e.preventDefault();
        this.togglePause();
      } else if (key === 'r') {
        e.preventDefault();
        this.callbacks.onRestart();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
  }
}
